import { BaseState } from './BaseState';
import { Subject, BehaviorSubject } from 'rxjs';
import { Shape } from '../types';
import { Line } from '../../models/shapes/Line';
import { Rectangle } from '../../models/shapes/Rectangle';
import { Circle } from '../../models/shapes/Circle';
import { SymLine } from '../../models/shapes/SymLine.js';
import { AbstractFrame } from '../../models/frames/AbstractFrame';
import { Point } from '../../models/Point';
import { s } from '../globalState/settings';
import { Block } from '../../models/shapes/Block';
import { getDisplayedShapes as getDisplayedShapesUtil, getSelectedShapes as getSelectedShapesUtil, getLinesIfSelected as getLinesIfSelectedUtil } from '../utils/shapeFilters';

/**
 * Manages drawing-related state and operations
 */
export class DrawingState extends BaseState {
  // RxJS subjects for reactive state management
  public shapes$ = new BehaviorSubject<Shape[]>([]);
  public selectedShapes$ = new BehaviorSubject<Shape[]>([]);
  public vertices$ = new BehaviorSubject<any[]>([]);

  private state = {
    // Shape collections
    shapes: [] as Shape[],
    activeShapes: [] as Shape[],
    
    // Template shapes for drawing
    line: new Line(Point.ORIGIN, Point.ORIGIN, [1, 0, 0, 1], 2),
    symline: new SymLine(Point.ORIGIN, Point.ORIGIN, [1, 0, 0, 1]),
    circle: new Circle(Point.ORIGIN, 0, [1, 0, 0, 1], 2),
    rectangle: new Rectangle(Point.ORIGIN, Point.ORIGIN, Point.ORIGIN, Point.ORIGIN, 0, 0, [1, 0, 0, 1], 2),
    free: new Block([], s.blockColor, s.blockThickness),
    selectFrame: new AbstractFrame(Point.ORIGIN, Point.ORIGIN, [0, 1, 0, 1]),
    
    // Drawing state
    selected: false,
    vertices: [] as any[],
  };

  constructor() {
    super();
  }

  getState() {
    return { ...this.state };
  }

  updateState(updates: Partial<typeof this.state>) {
    this.state = { ...this.state, ...updates };
    this.emitStateChange(this.state);
  }

  // Shape collection methods
  getShapes(): Shape[] {
    return [...this.state.shapes];
  }

  setShapes(shapes: Shape[]) {
    this.state.shapes = [...shapes];
    this.shapes$.next(this.state.shapes);
  }

  addShapes(shapes: Shape | Shape[]) {
    if (Array.isArray(shapes)) {
      this.state.shapes = this.state.shapes.concat(shapes);
    } else {
      this.state.shapes.push(shapes);
    }
    this.shapes$.next(this.state.shapes);
  }

  getDisplayedShapes(): Shape[] {
    return getDisplayedShapesUtil(this.state.shapes);
  }

  getSelectedShapes(): Shape[] {
    return getSelectedShapesUtil(this.state.shapes);
  }

  getLinesIfSelected(): Shape[] {
    return getLinesIfSelectedUtil(this.state.shapes);
  }

  getHiddenShapes(): Shape[] {
    return this.state.shapes.filter(s => s.hidden === true);
  }

  // Selection methods
  selectAllShapes() {
    this.getDisplayedShapes().forEach(s => s.isSelected = true);
    this.selectedShapes$.next(this.getSelectedShapes());
  }

  resetSelectedShapes() {
    this.state.shapes.filter(s => s.isSelected).forEach(s => s.isSelected = false);
    this.selectedShapes$.next([]);
  }

  displayHiddenShapes() {
    this.getHiddenShapes().forEach(s => s.hidden = false);
    this.shapes$.next(this.state.shapes);
  }

  hideSelectedShapes() {
    this.getSelectedShapes().forEach(s => {
      s.isSelected = false;
      s.hidden = true;
    });
    this.selectedShapes$.next([]);
    this.shapes$.next(this.state.shapes);
  }

  selectIfMouseInShapeBoundary(mouse: Point): boolean {
    const isinSelectBoundary = this.getDisplayedShapes().filter(s => s.isinSelectBoundary(mouse));
    if (isinSelectBoundary.length) {
      isinSelectBoundary?.forEach(s => {
        s.isSelected = !s.isSelected;
      });
      this.selectedShapes$.next(this.getSelectedShapes());
      return true;
    }
    return false;
  }

  // Template shape accessors
  getLine() { return this.state.line; }
  getSymLine() { return this.state.symline; }
  getCircle() { return this.state.circle; }
  getRectangle() { return this.state.rectangle; }
  getFree() { return this.state.free; }
  getSelectFrame() { return this.state.selectFrame; }

  // Vertices management
  getVertices() { return [...this.state.vertices]; }
  setVertices(vertices: any[]) { 
    this.state.vertices = [...vertices];
    this.vertices$.next(this.state.vertices);
  }
}
